import React, { useEffect, useState } from "react";
import {
  Tree,
  NodeModel,
  MultiBackend,
  getBackendOptions,
} from "@minoru/react-dnd-treeview";
import { DndProvider } from "react-dnd";
import {
  getAllFolders,
  getSubFolders,
  moveFolder,
} from "../../services/folderServices";
import FolderIcon from "@mui/icons-material/Folder";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { locale } from "../../utils/LocaleManager";
import { nodeStyle } from "./styled";
import { useFolderStore } from "../../stores/folderStore";
import { useContentStore } from "../../stores/contentStore";
import { darkGray } from "../../utils/StyleHelpers";
import { useRolePermissionsStore } from "../../stores/rolePermissionsStore";
import { Tooltip } from "@mui/material";
import { isTextNeedsEllipsis } from "../../utils/utilFunctions";
import { moveFolderRequest } from "../../models/folder";

const ROOT_FOLDER = {
  id: 1,
  parent: 0,
  text: locale.all_media,
  droppable: true,
  isOpen: true,
};

export const TreeView = ({
  isClickedBreadCrumb,
  setIsClickedBreadCrumb,
  isClickedFolder,
  onClickNewFolder,
}: any) => {
  const rolePermissions = useRolePermissionsStore(
    (state: any) => state.rolePermissions,
  );
  const [treeData, setTreeData] = useState<NodeModel[]>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<number>(ROOT_FOLDER.id);
  const { setIsLoading, setSelectedThumbnails, isShowMoveDialog } =
    useContentStore((state: any) => ({
      setIsLoading: state.setIsLoading,
      setSelectedThumbnails: state.setSelectedThumbnails,
      isShowMoveDialog: state.isShowMoveDialog,
    }));

  const {
    openedNodes,
    setFolderData,
    setFolderId,
    setBreadcrumbPath,
    setFolderName,
    setIsShowFolders,
    isRenameSuccess,
    isCreateSuccess,
    isDeleteSuccess,
    setPreviousFolderName,
    isMoveSuccess,
    setPreviousFolderId,
    isShowMoveFolder,
    isFolderMoveSuccess,
    setOpenFolderName,
  } = useFolderStore((state: any) => ({
    openedNodes: state.openedNodes,
    setFolderData: state.setFolderData,
    setFolderId: state.setFolderId,
    setBreadcrumbPath: state.setBreadcrumbPath,
    setFolderName: state.setFolderName,
    setIsShowFolders: state.setIsShowFolders,
    isRenameSuccess: state.isRenameSuccess,
    isCreateSuccess: state.isCreateSuccess,
    isDeleteSuccess: state.isDeleteSuccess,
    setPreviousFolderName: state.setPreviousFolderName,
    isMoveSuccess: state.isMoveSuccess,
    setPreviousFolderId: state.setPreviousFolderId,
    isShowMoveFolder: state.isShowMoveFolder,
    isFolderMoveSuccess: state.isFolderMoveSuccess,
    setOpenFolderName: state.setOpenFolderName,
  }));

  const updateTreeData = (newData: any[], parentId: number) => {
    if (!Array.isArray(newData)) return [];
    return newData.map((node: any) => ({
      ...node,
      parent: parentId,
      isOpen: node.id === ROOT_FOLDER.id ? ROOT_FOLDER.isOpen : node.isOpen,
    }));
  };

  const fetchROOT_FOLDERs = async () => {
    try {
      setIsLoading(true);
      const response = await getAllFolders(false);
      if (response?.status === 200) {
        const ROOT_FOLDERs = updateTreeData(response.data, ROOT_FOLDER.id);
        setTreeData([ROOT_FOLDER, ...ROOT_FOLDERs]);
        if (!isShowMoveDialog && !isShowMoveFolder) {
          setFolderData([...ROOT_FOLDERs]);
        }
      }
    } catch (error) {
      console.error("Error fetching root folders:", error);
    }
    setIsLoading(false);
  };

  const fetchSubFolders = async (item: any) => {
    try {
      setIsLoading(true);
      const response = await getSubFolders(item?.id);
      if (response?.status === 200) {
        const subFolders = updateTreeData(response.data, item.id);
        const newSubFolders = subFolders.filter(
          (subFolder: any) =>
            !treeData.some((treeItem) => treeItem.id === subFolder.id),
        );
        setTreeData((prevTreeData) => [...prevTreeData, ...newSubFolders]);
        if (newSubFolders.length === 0) {
          setFolderData([]);
        }
      }
    } catch (error) {
      console.error("Error fetching subfolders:", error);
    }
    setIsLoading(false);
  };

  const getUpdateFolderData = async (id: any) => {
    try {
      setIsLoading(true);
      setFolderId(id);
      const response = await getSubFolders(id);
      if (response?.status === 200) {
        const subFolders = updateTreeData(response.data, id);

        if (subFolders.length !== 0) {
          setIsShowFolders(true);
          setFolderData([...subFolders]);

          const newSubFolders = subFolders.filter(
            (subFolder: any) => subFolder.parent === id,
          );
          setTreeData((prevTreeData) => [
            ...prevTreeData.filter((treeItem: any) => treeItem.parent !== id),
            ...newSubFolders,
          ]);
        } else {
          setFolderData([]);
          setIsShowFolders(false);
        }
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching subfolders:", error);
    }
  };

  const fetchMoveFolders = async (id: string, request: moveFolderRequest) => {
    try {
      setIsLoading(true);
      await moveFolder(id, request);
    } catch (error) {
      console.error("Error moving folders:", error);
    }
    setIsLoading(false);
  };

  const handleDrop = (
    newTree: NodeModel[],
    { dragSourceId, dropTargetId }: any,
  ) => {
    if (!rolePermissions?.folder?.move) {
      return;
    }

    const id = dropTargetId === ROOT_FOLDER.id ? null : dropTargetId;
    const request: moveFolderRequest = { parentFolderId: id };

    fetchMoveFolders(dragSourceId, request);
    setTreeData(newTree);
  };

  const handleNodeClick = async (node: any) => {
    setIsLoading(true);
    setFolderId(node?.id);
    setSelectedNodeId(node?.id);
    setFolderName(node?.text);
    setOpenFolderName(node?.text);

    if (!isShowMoveDialog && !isShowMoveFolder) {
      setPreviousFolderName(node?.text);
      setPreviousFolderId(node.id);
      if (node?.id === ROOT_FOLDER?.id) {
        updateBreadcrumbPath(ROOT_FOLDER);
        setSelectedNodeId(ROOT_FOLDER?.id);
        setIsShowFolders(true);
        setSelectedThumbnails([]);
        const response = await getAllFolders(false);
        if (response?.status === 200) {
          const ROOT_FOLDERs = updateTreeData(response.data, ROOT_FOLDER.id);
          setFolderData(ROOT_FOLDERs); // Update folder data with root folder's children
        }
        return;
      }

      try {
        const response = await getSubFolders(node.id);
        if (response?.status === 200) {
          const subFolders = updateTreeData(response.data, node.id);
          if (subFolders.length !== 0) {
            setFolderData([...subFolders]);
            setIsShowFolders(true);
          } else {
            setFolderData([]);
            setIsShowFolders(false);
          }
        }
        updateBreadcrumbPath(node);
      } catch (error) {
        console.error("Error fetching subfolders:", error);
      }
    }

    setIsLoading(false);
  };

  const updateBreadcrumbPath = (node: NodeModel) => {
    if (!isShowMoveDialog && !isShowMoveFolder) {
      const path: NodeModel[] = [];
      let currentNode: NodeModel | undefined = node;
      while (currentNode) {
        path.unshift(currentNode);
        // eslint-disable-next-line no-loop-func
        currentNode = treeData.find((item) => item.id === currentNode?.parent);
      }
      setBreadcrumbPath(path);
    }
  };

  useEffect(() => {
    setFolderId(ROOT_FOLDER.id);
    fetchROOT_FOLDERs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    updateBreadcrumbPath(ROOT_FOLDER);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isClickedBreadCrumb) {
      if (isClickedBreadCrumb.id === ROOT_FOLDER.id) {
        fetchROOT_FOLDERs();
        updateBreadcrumbPath(ROOT_FOLDER);
        setSelectedNodeId(ROOT_FOLDER.id);
      }
      handleNodeClick(isClickedBreadCrumb);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClickedBreadCrumb]);

  useEffect(() => {
    if (isClickedFolder) {
      handleNodeClick(isClickedFolder);
      setFolderName(isClickedFolder);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClickedFolder]);

  useEffect(() => {
    if (
      isRenameSuccess ||
      isCreateSuccess ||
      isDeleteSuccess ||
      isMoveSuccess ||
      isFolderMoveSuccess
    ) {
      if (selectedNodeId === ROOT_FOLDER.id) {
        fetchROOT_FOLDERs();
      } else {
        getUpdateFolderData(selectedNodeId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isRenameSuccess,
    isCreateSuccess,
    isDeleteSuccess,
    isMoveSuccess,
    isFolderMoveSuccess,
  ]);

  return (
    <DndProvider backend={MultiBackend} options={getBackendOptions()}>
      <div className="custom-treeview">
        <Tree
          initialOpen={openedNodes}
          sort={true}
          tree={treeData}
          rootId={0}
          onDrop={handleDrop}
          render={(node, { depth, isOpen, onToggle }) => (
            <Tooltip
              title={isTextNeedsEllipsis(node.text) ? node.text : ""}
              placement="top-end"
              arrow
            >
              <div
                className="folder-item"
                style={{
                  marginLeft: depth * 20,
                  ...nodeStyle,
                  backgroundColor: selectedNodeId === node.id ? "#C1E7FE" : "",
                }}
                onClick={() => handleNodeClick(node)}
              >
                {node.droppable && (
                  <span
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                    >
                      <span
                        onClick={() => {
                          onToggle();
                          fetchSubFolders(node);
                        }}
                      >
                        {isOpen ? <ArrowDropDownIcon /> : <ArrowRightIcon />}
                      </span>
                      {node.parent === 0 ? (
                        <VideoLibraryIcon
                          style={{ color: darkGray }}
                          sx={{ width: "1.25rem", height: "1.25rem" }}
                        />
                      ) : (
                        <FolderIcon
                          style={{ color: darkGray }}
                          sx={{ width: "1.25rem", height: "1.25rem" }}
                        />
                      )}
                    </div>
                    <span
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {node.text}
                    </span>
                  </span>
                )}
              </div>
            </Tooltip>
          )}
        />
      </div>
    </DndProvider>
  );
};

export default TreeView;
